import React, { useState, useEffect } from 'react'
import AuthLayout from '../../../../layout/AuthLayout'
import { Box, Flex, Text, Group, TextInput, Button, Pagination, Modal, ScrollArea } from '@mantine/core'
import classes from '../../../../assets/module-css/AdminLayout.module.css'
import { useDispatch } from 'react-redux'
import { updateRoute } from '../../../../store/dataRoute'
import { getAllLanguagesList, deleteLanguage } from '../../../../services/languages'
import { useDebouncedCallback } from '@mantine/hooks'
import LoadingData from '../../../../component/ui/LoadingData'
import NoData from '../../../../component/ui/NoData'
import TableLanguage from '../../../../component/pages/Language/TableLanguage'
import FormLanguage from '../../../../component/pages/Language/FormLanguage'
import { notifications } from '@mantine/notifications'
import { IconCheck, IconX, IconPlus } from '@tabler/icons-react'
import { modals } from '@mantine/modals'

const defaultParameter = {
    'page[num]': 1,
    'page[limit]': 20,
}

const labelTable = [
    { label: 'ID', width: 80 },
    { label: 'Name', width: 200 },
    { label: 'Code', width: 150 },
    { label: 'Status', width: 100 },
    { label: 'Action', width: 100 },
]

const LanguageList = () => {
    const dispatch = useDispatch()
    const [params, setParams] = useState(defaultParameter)
    const [languageList, setLanguageList] = useState([])
    const [totalData, setTotalData] = useState(0)
    const [loading, setLoading] = useState(true)
    const [openModalForm, setOpenModalForm] = useState(false)
    const [detailData, setDetailData] = useState(null)

    const mappingRoute = [
        { label: '/', route: null },
        { label: 'Settings', route: null },
        { label: 'Language List', route: null },
    ]

    const handleGetAllLanguages = useDebouncedCallback(async () => {
        setLoading(true)
        try {
            const response = await getAllLanguagesList(params)

            if (response.success || response.data) {
                const startData = response.data || []
                const countData = response.meta?.pagination?.total || startData.length

                const remapLanguage = startData.map((val) => ({
                    id: val.attributes?.id || val.id,
                    name: val.attributes?.name || '',
                    code: val.attributes?.code || '',
                    status: val.attributes?.status ?? 1,
                }))

                setLanguageList(remapLanguage)
                setTotalData(countData)
            } else {
                setLanguageList([])
                setTotalData(0)
            }
        } catch (error) {
            console.log(error)
            setLanguageList([])
        } finally {
            setLoading(false)
        }
    }, 500)

    useEffect(() => {
        dispatch(updateRoute({ 'data': mappingRoute }))
        // eslint-disable-next-line
    }, [dispatch])

    useEffect(() => {
        handleGetAllLanguages()
        // eslint-disable-next-line
    }, [params])

    const handleDelete = (val) => {
        modals.openConfirmModal({
            title: 'Delete Language',
            children: (
                <Text size="sm">
                    Are you sure you want to delete language <strong>{val.name}</strong>? This action cannot be undone.
                </Text>
            ),
            labels: { confirm: 'Delete', cancel: 'Cancel' },
            confirmProps: { color: 'red' },
            onConfirm: async () => {
                try {
                    const response = await deleteLanguage(val.id)
                    if (response.success) {
                        notifications.show({
                            title: 'Success',
                            message: 'Language deleted successfully',
                            color: 'teal',
                            icon: <IconCheck />,
                        })
                        handleGetAllLanguages()
                    } else {
                        notifications.show({
                            title: 'Failed',
                            message: response.message || 'Failed to delete language',
                            color: 'red',
                            icon: <IconX />,
                        })
                    }
                } catch (error) {
                    notifications.show({
                        title: 'Error',
                        message: error.message || 'Something went wrong',
                        color: 'red',
                        icon: <IconX />,
                    })
                }
            },
        })
    }

    const actions = {
        edit: (val) => {
            setDetailData(val)
            setOpenModalForm(true)
        },
        delete: (val) => {
            handleDelete(val)
        }
    }

    const handleAction = (val, type) => {
        if (actions[type]) {
            actions[type](val)
        }
    }

    const handleChangeFilter = (name, val) => {
        setParams((oldVal) => ({ ...oldVal, [name]: val }))
    }

    const handleChangePage = (val) => {
        setParams((oldVal) => ({ ...oldVal, ['page[num]']: val }))
    }

    const onCloseModalForm = () => {
        setOpenModalForm(false)
        setDetailData(null)
    }

    return (
        <AuthLayout>
            <Box my={20}>
                <Box>
                    <Flex justify='space-between' align="center" mb={30}>
                        <Text className={classes.titleLayout}>Language List</Text>
                        <Group gap='sm' align='center'>
                            <TextInput
                                value={params['filter[name][like]'] || ''}
                                placeholder='Search by name'
                                onChange={(val) => {
                                    handleChangeFilter('filter[name][like]', val.target.value)
                                    handleChangePage(1)
                                }}
                                size='xs'
                            />
                            <Button size='xs' leftSection={<IconPlus size={14} />} onClick={() => { setDetailData(null); setOpenModalForm(true) }}>Add Language</Button>
                        </Group>
                    </Flex>
                </Box>

                <Box my={30}>
                    <Group mb={10} gap='xs'>
                        <Text fz={12}>Total Data Found: </Text>
                        <Text fz={12} fw='bold'>{totalData} Languages</Text>
                    </Group>
                    {loading ? <LoadingData /> : languageList.length > 0 ? <TableLanguage label={labelTable} data={languageList} actionMethod={handleAction} /> : <NoData />}
                </Box>
                <Flex justify='end'>
                    <Pagination size='sm' onChange={handleChangePage} value={params['page[num]']} total={totalData > 0 ? Math.ceil(totalData / params['page[limit]']) : 0} />
                </Flex>
            </Box>

            <Modal opened={openModalForm} onClose={onCloseModalForm} centered closeOnClickOutside={false} size='md' title={<Text fw='Bold'>{detailData ? 'Edit Language' : 'Add Language'}</Text>} scrollAreaComponent={ScrollArea.Autosize} withCloseButton={false}>
                <FormLanguage dataDetail={detailData} onCloseModal={onCloseModalForm} reloadList={handleGetAllLanguages} />
            </Modal>
        </AuthLayout>
    )
}

export default LanguageList
